// Vehicle presets for the EV Battery Sizing Calculator. Separate from the EV Powertrain
// Simulator's evSimulatorPresets.json on purpose: this tool takes a range + consumption
// figure (not road-load inputs), so each preset uses an independently measured consumption
// — EPA wall-side ratings, or the EV Powertrain Simulator's physics-based WLTC figure for
// the Tata cars (Autocar India's km/kWh is range ÷ pack kWh, so it would be circular).
// Each preset sizes a NEW pack (End-of-Life Capacity 100%) so it can be compared with the
// real pack. Validated 2026-09-25: required energy within −3% to +5% of the real packs.

import { DEFAULT_INPUTS } from './evBatteryTools';

const COMMON = {
  regenRecoveryPct: 0,
  avgSpeedKmph: 46.5,
  drivelineEffPct: 90,
  chargingEffPct: 88,
  auxPowerW: 300,
  motorInvEffPct: 92,
  cellToPackMassPct: 65,
  minSocPct: 3,
  maxSocPct: 98,
  eolCapacityPct: 100,
  coldDeratePct: 0,
};

const LFP_CELL = { cellNomV: 3.2, cellMinV: 2.5, cellMaxV: 3.65 };
const NMC_CELL = { cellNomV: 3.65, cellMinV: 2.5, cellMaxV: 4.15 };

export const GENERIC_PRESET_ID = 'generic';

export const BATTERY_SIZING_PRESETS = [
  {
    id: GENERIC_PRESET_ID,
    label: 'Generic compact EV (defaults)',
    reference: null,
    note: 'Generic 400 V NMC prismatic pack with an 80% end-of-life margin. Not a specific vehicle.',
    inputs: DEFAULT_INPUTS,
  },
  {
    id: 'nissan-leaf-40',
    label: 'Nissan Leaf 40 kWh',
    reference: '39.5 kWh NMC · 96S2P (350 V) · 110 kW · EPA 241 km',
    note: 'MY2019. Consumption: EPA combined 30 kWh/100 mi = 186 Wh/km at the wall. AESC 56.3 Ah cells. Estimated: continuous power, C-rates, cell mass, cell-to-pack ratio. The EPA-based requirement is about 5% above the real pack, so the tool rounds up to 3 parallel strings; the real car uses 2.',
    inputs: {
      ...COMMON, ...NMC_CELL, consumptionBasis: 'wall', rangeKm: 241, consumptionWhKm: 186, packVoltageV: 350,
      motorContKw: 80, motorPeakKw: 110, cellCapacityAh: 56.3, cRate: 3, pulseCRate: 6, cellMassKg: 0.92,
    },
  },
  {
    id: 'chevrolet-bolt',
    label: 'Chevrolet Bolt EV (60 kWh)',
    reference: '60 kWh NMC · 96S3P (350 V) · 150 kW · EPA 383 km',
    note: 'MY2017. Consumption: EPA combined 28 kWh/100 mi = 174 Wh/km at the wall. LG Chem pouch cells, capacity derived from 60 kWh ÷ (288 × 3.65 V). Estimated: continuous power, C-rates, cell mass, cell-to-pack ratio. The requirement is about 3% above the real pack, so the tool rounds up to 4 parallel strings; the real car uses 3.',
    inputs: {
      ...COMMON, ...NMC_CELL, consumptionBasis: 'wall', rangeKm: 383, consumptionWhKm: 174, packVoltageV: 350,
      motorContKw: 70, motorPeakKw: 150, cellCapacityAh: 57.1, cRate: 3, pulseCRate: 6, cellMassKg: 0.85,
    },
  },
  {
    id: 'tata-nexon-ev-max',
    label: 'Tata Nexon EV Max (40.5 kWh)',
    reference: '40.5 kWh LFP · 100S8P (320 V) · 105 kW · ARAI 437 km (MIDC) · real-world 263 km',
    note: 'Range: Autocar India real-world test (263 km). Consumption: 142 Wh/km battery-side from the EV Powertrain Simulator on WLTC. Tata AutoComp / Gotion 3.2 V 15.8 Ah cells. Estimated: continuous power, C-rates, cell mass, cell-to-pack ratio. Sizing to the MIDC 437 km instead needs about 25% more energy than the real pack: the MIDC figure is optimistic.',
    inputs: {
      ...COMMON, ...LFP_CELL, consumptionBasis: 'battery', rangeKm: 263, consumptionWhKm: 142, packVoltageV: 320,
      motorContKw: 55, motorPeakKw: 105, cellCapacityAh: 15.8, cRate: 3, pulseCRate: 5, cellMassKg: 0.29,
    },
  },
  {
    id: 'tata-curvv-ev-55',
    label: 'Tata Curvv EV 55 kWh',
    reference: '55 kWh LFP · Gotion 105 Ah cells · 123 kW · ARAI 585 km (MIDC) · real-world 365 km',
    note: 'Range: Autocar India real-world test (365 km). Consumption: 138 Wh/km battery-side from the EV Powertrain Simulator on WLTC, with estimated road load. Pack voltage and series/parallel layout are unpublished: 262 V (82S2P) is assumed, one layout that gives 55 kWh with 105 Ah cells (164S1P at ~525 V gives the same energy). Estimated: pack voltage, continuous power, C-rates, cell mass, cell-to-pack ratio.',
    inputs: {
      ...COMMON, ...LFP_CELL, consumptionBasis: 'battery', rangeKm: 365, consumptionWhKm: 138, packVoltageV: 262,
      motorContKw: 60, motorPeakKw: 123, cellCapacityAh: 105, cRate: 2, pulseCRate: 4, cellMassKg: 1.98,
    },
  },
  {
    id: 'tesla-model-3-rwd-lfp',
    label: 'Tesla Model 3 RWD (60 kWh LFP)',
    reference: '60 kWh LFP · 106S1P (339 V) · 208 kW · EPA 438 km (272 mi)',
    note: 'MY2022. Consumption: EPA combined 25 kWh/100 mi = 155 Wh/km at the wall. Cell capacity derived from 60 kWh ÷ (106 × 3.2 V). Estimated: continuous power, C-rates, cell mass, cell-to-pack ratio. The requirement is about 5% above the real pack, and with one very large cell per series position the tool must round up to 2 parallel strings. The real car uses 1.',
    inputs: {
      ...COMMON, ...LFP_CELL, consumptionBasis: 'wall', rangeKm: 438, consumptionWhKm: 155, packVoltageV: 339,
      motorContKw: 100, motorPeakKw: 208, cellCapacityAh: 176.9, cRate: 3, pulseCRate: 4.5, cellMassKg: 3.3,
    },
  },
];

export function findBatteryPreset(id) {
  return BATTERY_SIZING_PRESETS.find((p) => p.id === id) || null;
}
